import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useChartSimple } from '../../hooks/useChartSimple';
import { useSocket } from '../../hooks/useSocket';
import { ChartData } from '../../types';
import { getApiBaseUrl } from '../../utils/api';
import './ChartComponent.css';

interface ChartComponentProps {
  symbol: string;
  height?: number;
  selectedTimeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
}

export const ChartComponent: React.FC<ChartComponentProps> = ({ 
  symbol, 
  height = 400,
  selectedTimeframe = '1',
  onTimeframeChange
}) => {
  // Chart configuration
  // const CHART_CONFIG = {
  //   resolution: '1', // 1 minute
  //   candleCount: 100 // Number of candles to load (100 minutes = 1.7 hours)
  // };
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerReady, setContainerReady] = useState(false);
  const [internalTimeframe, setInternalTimeframe] = useState(selectedTimeframe);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Available timeframes
  const timeframes = [
    { value: '1', label: '1m' },
    { value: '5', label: '5m' },
    { value: '15', label: '15m' },
    { value: '30', label: '30m' },
    { value: '60', label: '1h' },
    { value: '240', label: '4h' },
    { value: 'D', label: '1d' }
  ];

  console.log('ChartComponent rendering, containerRef.current:', containerRef.current);
  
  const { 
    chart,
    candlestickSeries,
    setChartData,
    updateChartData
  } = useChartSimple(containerRef, containerReady, symbol);

  // Helper function to get resolution in seconds
  const getResolutionSeconds = useCallback((resolution: string): number => {
    const resolutionMap: { [key: string]: number } = {
      '1': 60,        // 1 minute
      '5': 5 * 60,    // 5 minutes
      '15': 15 * 60,  // 15 minutes
      '30': 30 * 60,  // 30 minutes
      '60': 60 * 60,  // 1 hour
      '240': 4 * 60 * 60, // 4 hours
      'D': 24 * 60 * 60   // 1 day
    };
    return resolutionMap[resolution] || 60;
  }, []);

  // Load chart data function - loads 500 candles for any timeframe
  const loadChartData = useCallback(async (resolution?: string) => {
    const timeframe = resolution || internalTimeframe;
    try {
      setIsLoading(true);
      setError(null); // Clear previous errors
      setDataLoaded(false); // Reset data loaded flag
      const backendUrl = getApiBaseUrl();
      console.log('Loading 500 candles for symbol:', symbol, 'resolution:', timeframe);
      
      // Calculate time range for 500 candles
      const now = Math.floor(Date.now() / 1000);
      const resolutionSeconds = getResolutionSeconds(timeframe);
      const timeRangeSeconds = resolutionSeconds * 500; // 500 candles
      const timeAgo = now - timeRangeSeconds;
      
      console.log(`Time range for 500 ${timeframe} candles: ${timeAgo} to ${now}`);
      
      const response = await fetch(`${backendUrl}/udf/history?symbol=${symbol}&resolution=${timeframe}&from=${timeAgo}&to=${now}`);
      const data = await response.json();
      
      if (data.s === 'ok' && data.t && data.t.length > 0) {
        const chartDataArray: ChartData[] = data.t.map((timestamp: number, index: number) => ({
          time: timestamp,
          open: data.o[index],
          high: data.h[index],
          low: data.l[index],
          close: data.c[index],
          volume: data.v[index]
        }));
        
        console.log(`Loaded ${chartDataArray.length} candles for ${timeframe} timeframe`);
        setChartData(chartDataArray);
        setDataLoaded(true);
      } else {
        setError('Failed to load chart data from API');
      }
    } catch (err) {
      console.error('Error loading chart data:', err);
      setError('Failed to load chart data');
    } finally {
      setIsLoading(false);
    }
  }, [symbol, internalTimeframe, setChartData, getResolutionSeconds]);

  // Handle timeframe change - optimized to prevent multiple re-renders
  const handleTimeframeChange = useCallback((timeframe: string) => {
    console.log('Timeframe changing to:', timeframe);
    
    // Batch state updates to prevent multiple re-renders
    setInternalTimeframe(timeframe);
    setDataLoaded(false);
    
    if (onTimeframeChange) {
      onTimeframeChange(timeframe);
    }
    
    // Reload chart data with new timeframe immediately
    loadChartData(timeframe);
  }, [onTimeframeChange, loadChartData]);

  const { 
    isConnected, 
    subscribe, 
    unsubscribe, 
    onChartData, 
    onError 
  } = useSocket();

  // Set container ready when ref is available
  useEffect(() => {
    if (containerRef.current) {
      console.log('Container ref is ready:', containerRef.current);
      setContainerReady(true);
    } else {
      console.log('Container ref not ready yet, will retry...');
      // Retry after a short delay
      const timer = setTimeout(() => {
        if (containerRef.current) {
          console.log('Container ref is ready (retry):', containerRef.current);
          setContainerReady(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [containerRef.current]);

  // Subscribe to symbol and timeframe when component mounts or timeframe changes
  useEffect(() => {
    if (isConnected && symbol) {
      const subscriptionKey = `${symbol}-${internalTimeframe}`;
      console.log('Subscribing to:', subscriptionKey);
      subscribe(subscriptionKey);
      setIsLoading(false);
    } else {
      console.log('Not connected or no symbol:', { isConnected, symbol });
    }

    return () => {
      if (symbol) {
        const subscriptionKey = `${symbol}-${internalTimeframe}`;
        console.log('Unsubscribing from:', subscriptionKey);
        unsubscribe(subscriptionKey);
      }
    };
  }, [isConnected, symbol, internalTimeframe, subscribe, unsubscribe]);

  // Debug effect to log chart state changes
  useEffect(() => {
    console.log('Chart state changed:', {
      containerReady,
      chart: !!chart,
      candlestickSeries: !!candlestickSeries,
      isConnected,
      symbol
    });
  }, [containerReady, chart, candlestickSeries, isConnected, symbol]);


  // Handle chart data updates
  useEffect(() => {
    const handleChartData = (data: ChartData[]) => {
      console.log('ChartComponent received real-time chart data:', data);
      if (data && data.length > 0) {
        console.log('Calling updateChartData with data length:', data.length);
        console.log('Chart ready:', !!chart);
        console.log('Series ready:', !!candlestickSeries);
        console.log('Data sample:', data[0]);
        console.log('Data OHLC values:', data.map(d => ({
          time: d.time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close
        })));
        updateChartData(data);
      } else {
        console.log('No data to update or empty data array');
      }
    };

    onChartData(handleChartData);

    return () => {
      // Cleanup will be handled by useSocket hook
    };
  }, [onChartData, updateChartData, chart, candlestickSeries]);

  // Handle errors
  useEffect(() => {
    const handleError = (errorMessage: string) => {
      setError(errorMessage);
      setIsLoading(false);
    };

    onError(handleError);

    return () => {
      // Cleanup will be handled by useSocket hook
    };
  }, [onError]);

  // Update internal timeframe when selectedTimeframe prop changes
  useEffect(() => {
    if (selectedTimeframe !== internalTimeframe) {
      console.log('Timeframe prop changed:', selectedTimeframe);
      setInternalTimeframe(selectedTimeframe);
      setDataLoaded(false);
    }
  }, [selectedTimeframe, internalTimeframe]);

  // Reset data loaded flag when symbol changes
  useEffect(() => {
    setDataLoaded(false);
  }, [symbol]);

  // Load initial chart data when symbol changes
  useEffect(() => {
    if (symbol && !dataLoaded) {
      console.log('Starting initial data load for symbol:', symbol);
      loadChartData(internalTimeframe);
    }
  }, [symbol, internalTimeframe, dataLoaded, loadChartData]);

  // Load data when chart becomes ready
  useEffect(() => {
    if (symbol && chart && candlestickSeries && !isLoading && !dataLoaded) {
      console.log('Chart is ready, loading data for symbol:', symbol);
      loadChartData(internalTimeframe);
    }
  }, [chart, candlestickSeries, symbol, internalTimeframe, isLoading, dataLoaded, loadChartData]);

  if (isLoading) {
    return (
      <div className="chart-container" style={{ height }}>
        <div className="chart-loading">
          <div className="spinner"></div>
          <p>Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container" style={{ height }}>
        <div className="chart-error">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container" style={{ height }}>
      <div 
        ref={containerRef} 
        className="chart-content" 
        style={{ 
          height: height, 
          width: '100%', 
          minHeight: '200px',
          backgroundColor: '#1e1e1e',
          border: '1px solid #2B2B2B',
          borderRadius: '4px'
        }}
        onLoad={() => console.log('Chart container loaded')}
      />
    </div>
  );
};
